import { AuthEvent } from './auth-event';
import { AuthState, GrantInProgress } from './auth-state';
import { decodeClaims, isDueForRefresh } from './claims';
import { Command } from './command';
import { AUTHORIZATION_RESPONSE_TTL_MS, CHALLENGE_TTL_MS, MAX_RESTORE_ATTEMPTS, RESTORE_BACKOFF_MS } from './constants';
import { AuthFailureCause, isRetryable } from './failure';
import { Provider, RefreshTrigger, ResourceTokens, SignInSource, SignOutReason } from './tokens';

export type TransitionResult = readonly [AuthState, readonly Command[]];

/**
 * What a `commandFailed` means to whatever was waiting on it.
 *
 * One value, because the event carries no diagnosis and no state can invent one — see
 * `AuthFailureCause.unexpectedFailure`. Every arm below attaches it for the same reason: a wait that
 * ends silently reads as a tap that did nothing.
 */
const UNEXPECTED: AuthFailureCause = { kind: 'unexpectedFailure' };

/**
 * The only place authentication rules live.
 *
 * Pure and synchronous: given a state, an event, and the current time, it returns the next
 * state and the side effects that should follow.
 *
 * Unmatched (state, event) pairs leave the state alone and emit a telemetry Command. They are
 * not errors: events arrive from the OS and the network, so a biometric prompt resolving after
 * Sign-out, or a refresh landing after a Company Switch, is normal. Throwing would crash on a
 * benign race, and treating them as failures could cause forced sign-outs.
 */
export function transition(state: AuthState, event: AuthEvent, now: number): TransitionResult {
  switch (state.tag) {
    case 'restoringSession':
      return fromRestoringSession(state, event, now);
    case 'unauthenticated':
      return fromUnauthenticated(state, event);
    case 'authorizing':
      return fromAuthorizing(state, event, now);
    case 'authorized':
      return fromAuthorized(state, event, now);
    case 'refreshing':
      return fromRefreshing(state, event);
    case 'restoreDeferred':
      return fromRestoreDeferred(state, event);
  }
}

// ---------------------------------------------------------------------------------------------
// restoringSession
// ---------------------------------------------------------------------------------------------

function fromRestoringSession(
  state: Extract<AuthState, { tag: 'restoringSession' }>,
  event: AuthEvent,
  now: number
): TransitionResult {
  const { phase } = state;

  switch (event.type) {
    case 'launchStateRead': {
      if (phase.kind !== 'readingLaunchState') return ignore(state, event);
      // A device that cannot store a session has nothing to read, and this is also the arm that keeps
      // a storage failure off the terminal path — `noSession` leaves the Vault intact where
      // `vaultUnavailable` would clear it.
      if (!event.storageUsable) {
        return [signedOut('noSession'), [{ do: 'navigate', to: 'signIn' }]];
      }
      return [{ ...state, phase: { kind: 'priming' } }, [{ do: 'primeTokenStorage' }]];
    }

    case 'tokenStorageLoaded': {
      if (phase.kind !== 'priming') return ignore(state, event);
      if (!event.hasSession) {
        return [signedOut('noSession'), [{ do: 'navigate', to: 'signIn' }]];
      }
      return startRestore(null, 'coldStart');
    }

    case 'primingFailed': {
      if (phase.kind !== 'priming') return ignore(state, event);
      switch (event.cause.kind) {
        case 'vaultUnavailable':
          return endSession('unrecoverable', { error: event.cause });
        default:
          // Everything else leaves the Vault intact and falls back to signing in. There is no prompt
          // to return to, so unlike the sign-in screen's biometric button there is nothing to stay
          // put for — and an OS lockout in particular says nothing about the Durable Root, which is
          // probably still good once the lockout lapses.
          return [signedOut('noSession', event.cause), [{ do: 'navigate', to: 'signIn' }]];
      }
    }

    // A launch is not a wait a person has to sit through: the splash screen can be beaten by a deep
    // link, and both of these are legitimate from there rather than errors.
    case 'nativeSignInRequested':
      return startNativeSignIn(event.email, event.password, 'password');
    case 'authorizationRequested':
      return startAuthorization(event.provider);

    /**
     * A redirect coming back into a new page. Accepted here and nowhere else: resuming is how a page
     * picks up a Grant it never started, and adopting a verifier out of storage over a live session
     * — or over a Grant already in progress — is the one thing it must not do.
     *
     * It rebuilds the Grant and falls straight into the validation the native path uses, so the
     * nonce comparison, the freshness bound and "never redeem a mismatched code" are shared rather
     * than written twice.
     */
    case 'authorizationResumed':
      return handleAuthorizationResponse(
        {
          kind: 'awaitingAuthorizationResponse',
          provider: event.provider,
          codeVerifier: event.codeVerifier,
          nonce: event.nonce,
          issuedAt: event.issuedAt,
        },
        event.params,
        now
      );

    /**
     * The launch could not be completed, and nobody can say why.
     *
     * Both phases here are a wait on a Command — `readLaunchState` and `primeTokenStorage` — and
     * behind both is the splash screen, which `selectSigningIn` keeps up for the whole of
     * `restoringSession`. So a dropped answer is a launch that never finishes, with no exception to
     * carve out: neither phase can be showing anything a person could act on instead.
     *
     * Signing in is the way out, and a cheap one: `noSession` clears no Vault, so the Durable Root
     * this device may still hold survives and the next launch attempts the restore again.
     */
    case 'commandFailed':
      return [signedOut('noSession', UNEXPECTED), [{ do: 'navigate', to: 'signIn' }]];

    /**
     * A refresh trigger during a launch, which is a launch already doing it.
     *
     * There are no tokens here to renew — that is what this state is for — and both phases are waiting
     * on a Command that will move them, so there is nothing to start and nothing stuck.
     *
     * Silent, like `authorizing` and `refreshing` before it. `SessionLifecycleEffects.refreshOnResume$`
     * asks on every foreground by design and never guards, which only works if the states that have
     * nothing to do say so without filing an `auth.illegal_transition` for it.
     */
    case 'refreshRequested':
      return [state, []];

    default:
      return ignore(state, event);
  }
}

// ---------------------------------------------------------------------------------------------
// unauthenticated
// ---------------------------------------------------------------------------------------------

function fromUnauthenticated(
  state: Extract<AuthState, { tag: 'unauthenticated' }>,
  event: AuthEvent
): TransitionResult {
  switch (event.type) {
    case 'nativeSignInRequested':
      return startNativeSignIn(event.email, event.password, 'password');
    case 'authorizationRequested':
      return startAuthorization(event.provider);

    /**
     * Someone tapped the biometric button.
     *
     * Whether credentials are actually stored is not checked here, and cannot be: the machine holds no
     * keychain contents. The screen offering the button decides — see `LoginCredentialsComponent` —
     * and the honest failure if it offers wrongly is a `biometricSignInFailed` the form renders.
     *
     * `idle` only. A second tap while a replay is already running is refused for the same reason
     * `refreshing` is a state: being here is the single-flight guard.
     *
     * A previous cause is dropped by building a fresh flow rather than by clearing a field.
     */
    case 'biometricSignInRequested':
      if (state.flow.kind !== 'idle') return ignore(state, event);
      return [{ ...state, flow: { kind: 'replayingCredentials' } }, [{ do: 'biometricSignIn' }]];

    /**
     * The keychain answered, so this is now an ordinary password sign-in.
     *
     * `fromStoredCredentials` is the only trace of where it came from, and it exists for the one rule
     * in {@link failGrant} that has to tell a stale stored password from a mistyped one.
     */
    case 'storedCredentialsRead':
      if (state.flow.kind !== 'replayingCredentials') return ignore(state, event);
      return startNativeSignIn(event.email, event.password, 'biometric');

    /**
     * The prompt was refused, or the keychain could not be read.
     *
     * Back to the form with the cause on it, and nothing else done: the reason and `vaultCleared` are
     * carried through untouched, because the screen this lands on reads both to decide whether to
     * offer the button again. Nothing is forgotten and nothing is cleared — see the
     * `forgetCredentials` Command for why a failed read is not an un-enrollment.
     */
    case 'biometricSignInFailed':
      if (state.flow.kind !== 'replayingCredentials') return ignore(state, event);
      return [{ ...state, flow: { kind: 'idle', error: event.cause } }, []];

    case 'signOutRequested':
      // Already signed out. Routinely produced by a double tap; not worth reporting.
      return [state, []];

    /**
     * A refresh trigger with no session behind it.
     *
     * `appResumed` fires on every foreground, and the sign-in screen is the screen an app is most
     * often backgrounded from — a password manager, most of all. There is nothing here to refresh —
     * this state holds no tokens, and it is the state the machine reaches by deciding there is no
     * session to restore — so the answer is to do nothing, in either flow.
     *
     * Silent for the same reason `authorizing` is silent: `ignore()` would file an
     * `auth.illegal_transition` every time someone returns to the sign-in screen, and that signal is
     * only worth reading because it holds nothing we put there. No `auth.refresh.coalesced` either —
     * that is `refreshing` reporting single-flight, and there is no refresh in flight to coalesce
     * with.
     */
    case 'refreshRequested':
      return [state, []];

    /**
     * A Command that will not answer, routed exactly where that flow's own failure event goes.
     *
     * `replayingCredentials` is the one flow this state holds a spinner over, so dropping it leaves the
     * biometric button spinning with no request behind it. It lands where `biometricSignInFailed`
     * lands rather than on a landing invented for this, which is the point: bouncing to a bare
     * sign-in form would cost someone what they were part-way through.
     *
     * `idle` ignores it, and deliberately: nothing was asked from there, and it may already be showing
     * a cause that this would overwrite with a vaguer one.
     */
    case 'commandFailed':
      if (state.flow.kind !== 'replayingCredentials') return ignore(state, event);
      return [{ ...state, flow: { kind: 'idle', error: UNEXPECTED } }, []];

    default:
      return ignore(state, event);
  }
}

// ---------------------------------------------------------------------------------------------
// authorizing
// ---------------------------------------------------------------------------------------------

function fromAuthorizing(
  state: Extract<AuthState, { tag: 'authorizing' }>,
  event: AuthEvent,
  now: number
): TransitionResult {
  const { grant } = state;

  // Abandoning a Grant is always allowed, whatever step it reached.
  if (event.type === 'signOutRequested') {
    return endSession('userRequested', { before: [{ do: 'closeExternalAgent' }, { do: 'cognitoSignOut' }] });
  }

  /**
   * A refresh trigger during a Grant, which is this app returning from something it opened itself
   * rather than a race worth knowing about.
   *
   * Federated sign-in hands the person to an **external user-agent** — an SFSafariViewController on
   * iOS, a Chrome Custom Tab on Android, opened by `openExternalAgent` so that what they type
   * at Google is never in our WebView. Opening it backgrounds the app, and returning fires
   * Capacitor's `resume`, which `SessionLifecycleEffects.refreshOnResume$` turns into `refreshRequested`
   * — so every federated sign-in produces one of these.
   *
   * Silent rather than ignored-and-logged: there is nothing here to refresh, because a Grant holds no
   * tokens and the session it is about to produce will be new. Reporting it would file an
   * `auth.illegal_transition` on every successful sign-in, and that signal is only worth reading
   * because it does not contain entries we manufactured.
   */
  if (event.type === 'refreshRequested') {
    return [state, []];
  }

  /**
   * A Grant Command that will not answer.
   *
   * Handled here rather than arm-by-arm because the answer is a property of how far the Grant got,
   * not of which Command it was — and every grant kind below issues at most one Command the machine
   * waits on, so there is never a question about which one this belongs to.
   *
   * One exception, and it is a state that is not waiting on a spinner:
   *
   * - **`challenge`.** The wait is a person typing — `selectChallenge` keeps the form on screen — so the
   *   code in their hand is still good and the Grant is still live at Cognito. Ending it would cost them
   *   the sign-in and a second SMS, which is the defect `4031226c` fixed from the other direction. It
   *   takes the cause and stays put, so retyping reissues `confirmChallenge`.
   *
   * `awaitingAuthorizationResponse` used to be a second exception, on the grounds that the Command it
   * was entered with had already answered and a callback is not a Command. Splitting
   * `openExternalAgent` out of the request made that false: it *is* issued from that state, through
   * {@link CommandRunner.attempt}, and a handler that threw unclassified there leaves a spinner over
   * the form with no agent on screen to produce anything. `closeExternalAgent` is still no risk — it
   * reaches {@link CommandRunner.teardown}, which raises none of these, because an agent that would not
   * close must not end a Grant that is going perfectly well.
   */
  if (event.type === 'commandFailed') {
    if (grant.kind === 'challenge') {
      return [{ ...state, grant: { ...grant, error: UNEXPECTED } }, []];
    }
    return failGrant(UNEXPECTED, grant, state.source);
  }

  switch (grant.kind) {
    case 'preparingAuthorization':
      switch (event.type) {
        /**
         * Record the Grant, then open the agent — in that order, and the order is the whole point.
         *
         * `SessionEffects` emits `stateAdvanced` before any `commandIssued`, so by the time
         * `openExternalAgent` runs, this state is already `awaitingAuthorizationResponse` and holds the
         * nonce a response will be checked against. Opening the agent from inside the handler that
         * reports the request instead — which is what `startAuthorizationRequest` used to do — left a
         * window the length of a modal presentation animation in which a callback had nowhere to land,
         * and a person already signed in at the provider comes back inside it.
         *
         * `url` is passed through and not stored: the state's job is to hold what validates the
         * response, and the request URL is not that.
         */
        case 'authorizationRequestPrepared':
          return [
            {
              ...state,
              grant: {
                kind: 'awaitingAuthorizationResponse',
                provider: event.provider,
                codeVerifier: event.codeVerifier,
                nonce: event.nonce,
                issuedAt: now,
              },
            },
            [{ do: 'openExternalAgent', url: event.url }],
          ];
        case 'grantFailed':
          return failGrant(event.cause, grant, state.source);
        default:
          return ignore(state, event);
      }

    case 'awaitingAuthorizationResponse':
      switch (event.type) {
        case 'authorizationResponseReceived':
          return handleAuthorizationResponse(grant, event.params, now);

        /**
         * The agent would not open, on a Grant this state had already recorded.
         *
         * The cost of issuing `openExternalAgent` from here rather than performing it before the state
         * advanced: the failure now arrives in `awaitingAuthorizationResponse` instead of in
         * `preparingAuthorization`. Ending the Grant is the same answer that arm gives it, and for the
         * same reason — `selectSigningIn` holds a spinner over both, so a device that cannot open a
         * user-agent has to reach the password form rather than wait for a callback that cannot come.
         *
         * No `closeExternalAgent`: nothing opened.
         */
        case 'grantFailed':
          return failGrant(event.cause, grant, state.source);

        /**
         * The agent is off screen. That is all this event can be trusted to mean.
         *
         * It does not mean a person dismissed the agent. On Android, `@capacitor/browser` infers it
         * from an `EventGroup` counter fed by Chrome's `TAB_SHOWN`/`TAB_HIDDEN` and the app's own
         * `handleOnResume`/`handleOnPause`, and a password manager opened over the provider's page
         * drains that counter without the tab closing. Measured on a Pixel 8 Pro with Bitwarden:
         * eight of these in one session, and the genuine callback arriving fifteen seconds after one
         * of them.
         *
         * So two things hold here:
         *
         * - **The Grant is kept.** The verifier and nonce are the only things that can redeem the
         *   code, the freshness bound still applies, and the nonce is still compared — a late
         *   response is validated exactly as a prompt one is.
         * - **No `closeExternalAgent`.** On `@capacitor/browser` 8 that is implemented on Android
         *   (it starts `BrowserControllerActivity` with `close`), so it is either a no-op because
         *   the agent really did close, or it is the defect — dismissing a sign-in still in progress.
         *
         * Nothing is reported. A deliberate close raises this too, and the counter fires at most
         * once per `Browser.open()`, so there is no rate here worth alerting on.
         */
        case 'authorizationAbandoned':
          if (grant.agentGone) return [state, []];
          return [{ ...state, grant: { ...grant, agentGone: true } }, []];

        /**
         * Sign in again, replacing a Grant whose agent has gone.
         *
         * Only once the agent is gone: until then the form is behind a spinner, so nothing can ask
         * for this and it stays reportable.
         */
        case 'authorizationRequested':
          if (!grant.agentGone) return ignore(state, event);
          return startAuthorization(event.provider);

        // The other two buttons on that form, admitted on the same condition and for the same
        // reason. Refusing them leaves the sign-in screen rendered with every control enabled and
        // none of them working, which reads as a tap that was received and ignored — and files an
        // `auth.illegal_transition` for a choice someone was invited to make.
        case 'nativeSignInRequested':
          if (!grant.agentGone) return ignore(state, event);
          return startNativeSignIn(event.email, event.password, 'password');

        case 'biometricSignInRequested':
          if (!grant.agentGone) return ignore(state, event);
          return [
            {
              tag: 'unauthenticated',
              reason: 'noSession',
              flow: { kind: 'replayingCredentials' },
              vaultCleared: false,
            },
            [{ do: 'biometricSignIn' }],
          ];

        default:
          return ignore(state, event);
      }

    case 'requestingTokens':
      switch (event.type) {
        case 'idTokenObtained':
          return exchangeIdToken(event.idToken, state.source);
        case 'grantFailed':
          return failGrant(event.cause, grant, state.source);
        default:
          return ignore(state, event);
      }

    case 'nativeSignIn':
      switch (event.type) {
        case 'challengeIssued':
          return [
            {
              ...state,
              grant: { kind: 'challenge', challenge: event.challenge, expiresAt: now + CHALLENGE_TTL_MS },
            },
            [],
          ];
        case 'idTokenObtained':
          return exchangeIdToken(event.idToken, state.source);
        case 'grantFailed':
          return failGrant(event.cause, grant, state.source);
        default:
          return ignore(state, event);
      }

    case 'challenge':
      switch (event.type) {
        case 'challengeAnswered':
          if (now >= grant.expiresAt) {
            // Cognito's auth-flow session has lapsed. Fail explicitly rather than letting it
            // come back as an opaque server error.
            return failGrant({ kind: 'challengeExpired' }, grant, state.source);
          }
          return [{ ...state, grant: { ...grant, error: undefined } }, [{ do: 'confirmChallenge', code: event.code }]];

        /**
         * Cognito drew a second Challenge in answer to the first.
         *
         * `confirmChallenge` finishes a Grant *or* draws another one — that is what its own docstring
         * says, and `eventFor` turns the second case into this event — so `challengeIssued` is an
         * answer to a Command this state issues while staying put. Without an arm here the first
         * Challenge was accepted and the second silently dropped, leaving the screen collecting a code
         * for a Challenge Cognito had already superseded and nothing left in flight to move it.
         *
         * A fresh Grant rather than a patched one, mirroring the `nativeSignIn` arm above: the new
         * Challenge gets the full TTL, because Cognito's auth-flow session restarts its clock with it,
         * and any error from the previous code goes with the Challenge it belonged to.
         *
         * A genuine SMS→TOTP chain is unusual in today's pool config, so this is rare rather than
         * unreachable — and it becomes ordinary the moment that config changes.
         */
        case 'challengeIssued':
          return [
            {
              ...state,
              grant: { kind: 'challenge', challenge: event.challenge, expiresAt: now + CHALLENGE_TTL_MS },
            },
            [],
          ];

        case 'idTokenObtained':
          return exchangeIdToken(event.idToken, state.source);
        case 'grantFailed':
          // Every recoverable kind stays on the challenge. A wrong code is the obvious one. An answer
          // that never arrived is another: Cognito's auth-flow session outlives a dropped packet and
          // the code in someone's hand is still the right one, so ending the Grant would cost them the
          // sign-in and a second code for a failure `isRetryable` already says "must never discard a
          // session". A throttle says nothing about the code either — it is still good once the
          // throttle clears, and there is no way to reissue one. It is named here rather than folded
          // into `isRetryable`, which the Session Restore and backoff paths read as well. Anything
          // else means the attempt itself is over.
          if (
            event.cause.kind === 'challengeFailed' ||
            event.cause.kind === 'rateLimited' ||
            isRetryable(event.cause)
          ) {
            return [{ ...state, grant: { ...grant, error: event.cause } }, []];
          }
          return failGrant(event.cause, grant, state.source);
        default:
          return ignore(state, event);
      }

    // One case for both: whether the Resource Server was handed a Cognito ID Token or an Azure code,
    // what it answers is a session, and there is nothing below here that knows which Grant it came
    // from.
    case 'exchangingIdToken':
    case 'exchangingMicrosoftCode':
      switch (event.type) {
        case 'resourceTokensObtained':
          return [
            { tag: 'authorized', tokens: event.tokens, task: { kind: 'idle' } },
            [
              { do: 'notifyAppSignedIn', via: 'signIn', source: state.source },
              { do: 'navigate', to: 'home' },
            ],
          ];
        case 'grantFailed':
          return failGrant(event.cause, grant, state.source);
        default:
          return ignore(state, event);
      }
  }
}

/**
 * Validate an authorization response and move on, or fail.
 *
 * The expected nonce comes from state, never from the event. The callback arrives on an
 * unclaimed custom scheme that any app on the device can invoke, so nothing in `params` is
 * trusted — including any claim about what the nonce should have been.
 *
 * The nonce is therefore checked first, before any other parameter is read. `error` is a parameter
 * like any other: a callback that says the provider refused us, on a nonce we never issued, is not
 * our provider talking. RFC 6749 §4.1.2.1 requires `state` on the error response whenever the
 * request carried one, so a refusal that omits it is not a well-formed answer to this Grant.
 */
function handleAuthorizationResponse(
  grant: Extract<GrantInProgress, { kind: 'awaitingAuthorizationResponse' }>,
  params: URLSearchParams,
  now: number
): TransitionResult {
  const close: Command[] = [{ do: 'closeExternalAgent' }];

  const nonce = params.get('state');
  if (nonce === null || nonce !== grant.nonce) {
    return failGrant({ kind: 'stateMismatch' }, grant, grant.provider, close);
  }

  if (params.get('error')) {
    return failGrant({ kind: 'authorizationAbandoned' }, grant, grant.provider, close);
  }

  if (now - grant.issuedAt > AUTHORIZATION_RESPONSE_TTL_MS) {
    // Too old to be the response we are waiting for: a replay, or a stale deep link.
    return failGrant({ kind: 'stateMismatch' }, grant, grant.provider, close);
  }

  const code = params.get('code');
  if (!code) {
    return failGrant({ kind: 'authorizationAbandoned' }, grant, grant.provider, close);
  }

  /**
   * Who redeems the code, which is what the verifier's presence records.
   *
   * With one, this app redeems it at Cognito's token endpoint and the ID Token that comes back goes
   * to the Token Exchange. Without one, the Resource Server redeems it at Azure — it holds that
   * client secret and we do not — so the code goes to the Token Exchange as it stands.
   *
   * Branching on the verifier rather than on `provider` is deliberate, and narrower than it may
   * look: it is the field the next line actually needs, so this branch and its body cannot fall out
   * of step. It is **not** a claim that the provider is unknown here — `prepareAuthorizationRequest`
   * decides by provider which material to generate, and `takePendingAuthorization` refuses a stored
   * Grant whose two disagree. Those are where the pairing is enforced; this only consumes it.
   *
   * This moves back to `provider` once the Microsoft leg has a verifier of its own — which needs a
   * Resource Server field to carry it — at which point there is a real choice to make rather than a
   * field that is present or absent.
   */
  if (grant.codeVerifier === undefined) {
    return [
      { tag: 'authorizing', grant: { kind: 'exchangingMicrosoftCode', code }, source: grant.provider },
      [...close, { do: 'exchangeMicrosoftCode', code }],
    ];
  }

  return [
    {
      tag: 'authorizing',
      grant: { kind: 'requestingTokens', provider: grant.provider, code, codeVerifier: grant.codeVerifier },
      source: grant.provider,
    },
    [...close, { do: 'requestTokens', code, codeVerifier: grant.codeVerifier }],
  ];
}

// ---------------------------------------------------------------------------------------------
// authorized
// ---------------------------------------------------------------------------------------------

function fromAuthorized(
  state: Extract<AuthState, { tag: 'authorized' }>,
  event: AuthEvent,
  now: number
): TransitionResult {
  switch (event.type) {
    case 'accessTokenRejected':
      return startRestore(state.tokens, 'accessRejected');

    /**
     * A refresh nobody asked for must not cancel work someone did ask for.
     *
     * Reached the same way a Grant reaches it: enrolling biometrics raises an OS prompt, which
     * backgrounds the app, and coming back is a `resume` — so the enrollment was being thrown away
     * mid-flight, and the `biometricEnrollmentFinished` that followed then arrived in `refreshing`
     * where nothing handles it. The toggle only appeared to work because the restore lands back on
     * `authorized{idle}` a moment later; a `taskFailed` would have been swallowed in silence.
     *
     * Dropping the refresh is safe rather than merely convenient: the interceptor refreshes before
     * the next request it sends, and the next resume or 401 asks again. `accessTokenRejected` is
     * deliberately *not* treated this way — there the token has actually been refused, so a restore
     * has to happen even at the cost of the task.
     *
     * **Every trigger but `preflightSkew` is gated on the refresh window.** A resume and a
     * reconnection are both something happening near the app rather than a token needing renewal, and
     * they arrive constantly — a foreground, a lift, a lift again. Ungated they spend two round-trips
     * on a token with hours of life left, hold every in-flight navigation behind `isRestoring`, and
     * book a `sessionEstablished('refresh')` and an `auth.refresh` vital that count returns to the app
     * rather than refreshes. `preflightSkew` is the exception because it arrives *from* a due-check:
     * gating it would strand the request that asked for it.
     */
    case 'refreshRequested':
      if (state.task.kind !== 'idle') return [state, []];
      if (event.trigger !== 'preflightSkew' && !isDueForRefresh(decodeClaims(state.tokens.accessToken), now))
        return [state, []];
      return startRestore(state.tokens, event.trigger);

    case 'companySwitchRequested':
      return [
        {
          tag: 'refreshing',
          stale: state.tokens,
          trigger: 'companySwitch',
          attempt: 0,
          companyUserId: event.companyUserId,
        },
        [{ do: 'sessionRestore', trigger: 'companySwitch' }],
      ];

    case 'signOutRequested':
      return endSession('userRequested', { before: [{ do: 'cognitoSignOut' }] });

    // No `cognitoSignOut` and no `clearVault` — see `CLEARS_VAULT`. The session ends because the app
    // cannot use it, not because anything is wrong with the credentials, so they survive and the
    // next launch restores from them silently.
    case 'sessionUnusable':
      return endSession('sessionUnusable', { error: event.cause });

    case 'biometricEnrollmentRequested':
      if (state.task.kind !== 'idle') return ignore(state, event);
      return [
        { ...state, task: { kind: 'enrollingBiometrics' } },
        [{ do: 'enrollBiometrics', enabled: event.enabled }],
      ];

    case 'biometricEnrollmentFinished':
      if (state.task.kind !== 'enrollingBiometrics') return ignore(state, event);
      return [{ ...state, task: { kind: 'idle' } }, []];

    case 'taskFailed':
      // Ends the task rather than staying put, so the control that started it is usable again, and
      // carries the cause, because nothing else would report it.
      //
      // Staying put is what a failed attempt used to do, and it made the attempt unrepeatable: a task
      // is what `biometricEnrollmentRequested` refuses to start behind, so a refused enrollment held
      // the screen against the very retry it was asking for — the second submit produced no command,
      // no state change, and a `reportIllegal`. Nothing but a relaunch cleared it, and until then
      // every proactive refresh was dropped by the same task guard. `authorized{idle}` carrying an
      // error exists for exactly this.
      if (state.task.kind !== 'enrollingBiometrics') return ignore(state, event);
      return [{ ...state, task: { kind: 'idle', error: event.cause } }, []];

    /**
     * A task Command that will not answer, ended the same way `taskFailed` ends one.
     *
     * The session is not in question — a handler threw, and the tokens this state holds are as good
     * as they were. What matters is that the task ends: a running task refuses the next
     * `biometricEnrollmentRequested` and swallows every proactive `refreshRequested` behind the same
     * guard, so one that never ends holds its screen against the retry it is asking for until the app
     * is relaunched.
     */
    case 'commandFailed':
      if (state.task.kind !== 'enrollingBiometrics') return ignore(state, event);
      return [{ ...state, task: { kind: 'idle', error: UNEXPECTED } }, []];

    default:
      return ignore(state, event);
  }
}

// ---------------------------------------------------------------------------------------------
// refreshing
// ---------------------------------------------------------------------------------------------

function fromRefreshing(state: Extract<AuthState, { tag: 'refreshing' }>, event: AuthEvent): TransitionResult {
  switch (event.type) {
    // Single-flight. A restore is already running, so there is nothing to do and nothing wrong.
    // Ten queued requests failing at once produce ten of these; one restore happens.
    // Deliberately silent rather than reported — this is the expected path, not an anomaly.
    case 'accessTokenRejected':
      return [state, []];
    case 'refreshRequested':
      return [state, []];

    case 'resourceTokensObtained': {
      const commands: Command[] = [];

      if (state.trigger === 'companySwitch') {
        // Full reload, so no company-scoped state can survive into the new company.
        return [{ tag: 'authorized', tokens: event.tokens, task: { kind: 'idle' } }, [{ do: 'reloadApp' }]];
      }

      if (state.stale === null) {
        // Cold start: the rest of the app has not been told there is a session yet.
        commands.push({ do: 'notifyAppSignedIn', via: 'restore' }, { do: 'navigate', to: 'home' });
      }

      return [{ tag: 'authorized', tokens: event.tokens, task: { kind: 'idle' } }, commands];
    }

    case 'refreshFailed':
      return classifyRefreshFailure(state, event.cause);

    case 'signOutRequested':
      return endSession('userRequested', { before: [{ do: 'cognitoSignOut' }] });

    /**
     * `sessionRestore` will not answer, so stop waiting on it.
     *
     * Not a refusal and not a retry: a handler threw, which says nothing about the Durable Root and
     * will most likely throw again. {@link keepOrAbandonSession} is the same landing a restore that
     * ran out of attempts gets, and for the same reason — a session already in hand is not lost over
     * a refresh that failed, and a cold start with none has to show the sign-in form.
     */
    case 'commandFailed':
      return keepOrAbandonSession(state, UNEXPECTED);

    /**
     * A task someone asked for, arriving while a restore is running.
     *
     * The courtesy `fromAuthorized` already extends in the other direction, where a background
     * refresh is dropped instead of being allowed to cancel work someone asked for. The reverse was
     * not protected: both fell through to `ignore()`, and `refreshing` is common and short-lived —
     * foreground the app, go to Settings, tap — so ordinary use reaches it.
     *
     * The refusal is recorded and the request is not parked, which is the cheap half of the two
     * available fixes. Deferring the task until the restore settles would need somewhere to hold the
     * direction, which is a poor trade for a race this brief. Recording it costs one optional cause
     * and makes the tap say something instead of nothing.
     *
     * A refusal is also the answer the switcher wants: `ignore()` files an `auth.illegal_transition`
     * for a tap that is legal, and `appResumed` fires on every foreground, so the switch opened
     * straight after one put entries in the log whose value depends on holding nothing we put there.
     *
     * Nothing is started and nothing is lost: the restore continues untouched, and a second tap once
     * it lands behaves exactly as it always did.
     */
    case 'biometricEnrollmentRequested':
    case 'companySwitchRequested':
      return [{ ...state, taskRefused: { kind: 'sessionBusy' } }, []];

    default:
      return ignore(state, event);
  }
}

/**
 * Stop refreshing without deciding anything about the credentials.
 *
 * Three outcomes, and the Vault is untouched in all of them, so the next launch restores normally.
 *
 * With a session to fall back on, the failed refresh costs nothing — the interceptor asks again
 * before the next request, and so does the next resume.
 *
 * With nothing behind it, this is a cold start, and what happens next turns on whether anybody
 * refused us. An unreachable server did not: the Durable Root is very likely still good, so the
 * restore is deferred and retried when the network comes back. Anything else at a cold start has no
 * retry that would behave differently, and the sign-in form is the only thing left to show.
 */
function keepOrAbandonSession(
  state: Extract<AuthState, { tag: 'refreshing' }>,
  cause: AuthFailureCause
): TransitionResult {
  if (state.stale !== null) {
    return [{ tag: 'authorized', tokens: state.stale, task: { kind: 'idle' } }, []];
  }
  if (cause.kind === 'network' || cause.kind === 'deviceOffline') {
    return [{ tag: 'restoreDeferred', trigger: state.trigger }, [{ do: 'navigate', to: 'offline' }]];
  }
  return [signedOut('noSession', cause), [{ do: 'navigate', to: 'signIn' }]];
}

/**
 * Decide what a failed Session Restore means.
 *
 * Most refresh failures are transient, so they are retried rather than read as a Sign-out. Only a
 * genuine refusal ends the session.
 */
function classifyRefreshFailure(
  state: Extract<AuthState, { tag: 'refreshing' }>,
  cause: AuthFailureCause
): TransitionResult {
  // A Company Switch is user-initiated and easily retried by hand. Failing one must never
  // disturb the session they already had.
  if (state.trigger === 'companySwitch' && state.stale !== null) {
    return [{ tag: 'authorized', tokens: state.stale, task: { kind: 'idle' } }, []];
  }

  if (isRetryable(cause)) {
    const attempt = state.attempt + 1;
    if (attempt < MAX_RESTORE_ATTEMPTS) {
      return [
        { ...state, attempt },
        [{ do: 'sessionRestore', trigger: state.trigger, afterMs: RESTORE_BACKOFF_MS[attempt] }],
      ];
    }
    // Out of attempts, but still not a Sign-out: an unreachable network is not a revoked token.
    return keepOrAbandonSession(state, cause);
  }

  // Exhaustive, and deliberately not a `default`. Ending a session here destroys the Durable Root
  // *and* revokes the Cognito session with it, which is the most expensive thing the machine can
  // do — so a cause nobody has classified should fail to compile rather than fall into it.
  switch (cause.kind) {
    case 'resourceServerRejected':
    case 'refreshRejected':
      // The Authorization Server session may still be valid, so tear it down explicitly —
      // otherwise the app rehydrates it on the next launch and loops forever.
      return endSession('refreshRejected', { error: cause, before: [{ do: 'cognitoSignOut' }] });

    // A prompt refused, or refused by the OS, destroys no key material. Not reachable while
    // refresh token rotation is off — the token never changes, so `VaultTokenStorage` skips the
    // write and no prompt is raised — but turning rotation on makes every refresh a gated write,
    // and the answer then has to be this rather than a Forced Sign-out.
    //
    // `endSession` when tokens were live, because the app has an identity, an Intercom session and a
    // push registration attached to them and `notifyAppSignedOut` is what tears those down. It is
    // the reason that decides the wipe, not the helper: `CLEARS_VAULT.noSession` is false, so the
    // Durable Root survives exactly as the paragraph above requires. A cold start reaches this with
    // `stale === null` and nothing signed in, so it stays quiet rather than resetting global state
    // for someone who never had any.
    case 'biometricCancelled':
    case 'biometricRejected':
    case 'biometricLockedOut':
      return state.stale !== null
        ? endSession('noSession', { error: cause })
        : [signedOut('noSession', cause), [{ do: 'navigate', to: 'signIn' }]];

    // Grouped with the prompt failures rather than with the terminal causes below, and the grouping
    // is the whole decision: this cause exists because nobody could say what went wrong, and the one
    // thing that must not follow from "we do not know" is destroying the Durable Root. No handler
    // builds a `refreshFailed` carrying it today — `commandFailed` is answered before this is
    // reached — but the switch is total by design, and the safe answer is the one written down.
    case 'unexpectedFailure':
      return keepOrAbandonSession(state, cause);
    // Nothing was attempted, so there is nothing to retry. `keepOrAbandonSession` gives the two
    // answers that matter: a live session survives untouched, and a cold start defers.
    case 'deviceOffline':
      return keepOrAbandonSession(state, cause);
    // Nor can this one: `sessionBusy` is raised *by* a refresh refusing a task, so a refresh failing
    // with it would mean the restore reporting its own guard as the reason it could not run.
    case 'sessionBusy':
      return keepOrAbandonSession(state, cause);
    // Nor this one, today: only the sign-in and Challenge classifiers produce it, and a Session
    // Restore reads neither. Written to the safe answer anyway — a throttled refresh is the server
    // declining to answer, which is no evidence at all about the Durable Root.
    case 'rateLimited':
      return keepOrAbandonSession(state, cause);

    // Three groups, all ending the session. `network` and `malformedResponse` are answered by the
    // retry branch above and cannot arrive here — listed so the compiler, rather than a reader, is
    // what proves it. `vaultUnavailable` is a Vault with nothing left worth keeping. The rest belong
    // to a Grant, so reaching a refresh means the machine is somewhere it should not be, and the safe
    // reading of that is a session no longer trusted.
    case 'network':
    case 'malformedResponse':
    case 'vaultUnavailable':
    case 'invalidCredentials':
    case 'storedCredentialsRejected':
    case 'challengeFailed':
    case 'challengeExpired':
    case 'stateMismatch':
    case 'authorizationAbandoned':
    case 'authorizationFailed':
    case 'unsupportedChallenge':
      return endSession('unrecoverable', { error: cause, before: [{ do: 'cognitoSignOut' }] });
  }
}

// ---------------------------------------------------------------------------------------------
// restoreDeferred
// ---------------------------------------------------------------------------------------------

/**
 * A restore waiting for the world to change.
 *
 * Both refresh triggers that can reach here mean the same thing — something happened that might have
 * fixed the network — so both restart the restore. `attempt` goes back to zero: the ladder measures
 * one stretch of unreachability, and a device that has since reconnected is starting a new one.
 *
 * The Command is *computed* from the trigger the state carries rather than replayed from what failed.
 * That is the difference between a machine whose behaviour is readable from `transition()` and one
 * where Commands come out of stored state.
 *
 * A sign-in from here is allowed and deliberately so. The offline screen is not a trap: someone who
 * would rather sign in as a different person should be able to, and the Grant fails honestly if the
 * network is still down. Everything else falls through to `ignore()`, which is the machine's standard
 * answer to an event that means nothing in the current state.
 */
function fromRestoreDeferred(
  state: Extract<AuthState, { tag: 'restoreDeferred' }>,
  event: AuthEvent
): TransitionResult {
  switch (event.type) {
    case 'refreshRequested':
      return [
        { tag: 'refreshing', stale: null, trigger: state.trigger, attempt: 0 },
        [{ do: 'sessionRestore', trigger: state.trigger, afterMs: 0 }],
      ];
    case 'nativeSignInRequested':
      return startNativeSignIn(event.email, event.password, 'password');
    case 'authorizationRequested':
      return startAuthorization(event.provider);
    default:
      return ignore(state, event);
  }
}

// ---------------------------------------------------------------------------------------------
// Shared builders
// ---------------------------------------------------------------------------------------------

/**
 * Whether ending the session for this reason should also destroy the Durable Root.
 *
 * `noSession` must not: it is reached from a biometric lockout and from a cold start with no
 * network, where the stored session is very likely still valid. Clearing the Vault there would
 * turn a temporary problem into a permanent Forced Sign-out.
 *
 * `sessionUnusable` must not either, for the same reason from the other direction: the credentials
 * were never in question, so destroying them makes a server outage cost someone their password.
 */
const CLEARS_VAULT = {
  userRequested: true,
  refreshRejected: true,
  unrecoverable: true,
  noSession: false,
  sessionUnusable: false,
} as const satisfies Record<SignOutReason, boolean>;

function signedOut(reason: SignOutReason, error?: AuthFailureCause, vaultCleared = false): AuthState {
  return { tag: 'unauthenticated', reason, flow: { kind: 'idle', error }, vaultCleared };
}

function endSession(
  reason: SignOutReason,
  opts: { error?: AuthFailureCause; before?: readonly Command[] } = {}
): TransitionResult {
  const clearVault = CLEARS_VAULT[reason];
  const commands: Command[] = [...(opts.before ?? [])];
  if (clearVault) commands.push({ do: 'clearVault' });
  commands.push({ do: 'notifyAppSignedOut', reason }, { do: 'navigate', to: 'signIn' });
  return [signedOut(reason, opts.error, clearVault), commands];
}

/**
 * End a failed Grant.
 *
 * Two things it must not destroy, for the same reason: a Grant is attempted *from* the sign-in
 * screen, and a device sitting on that screen may still hold a perfectly good Durable Root — a
 * cold-start restore refused for anything but the network lands exactly there.
 *
 * So no `clearVault`, ever. And `cognitoSignOut` only once the Authorization Server has actually
 * issued something, which is only at `exchangingIdToken`. Amplify's `signOut()` removes its refresh
 * token through the Vault-backed storage *and* revokes it at the Authorization Server, so signing
 * out a Grant that never got a session is how a mistyped password turns into a Forced Sign-out.
 * Where it is still needed: a Grant the Resource Server refused leaves a live Cognito session that
 * the app would rehydrate and retry forever.
 *
 * The one thing it *does* destroy is a stored password the Authorization Server has just refused. That
 * is the only rule reading `fromStoredCredentials`, and it is narrow deliberately: `invalidCredentials`
 * means the password was changed elsewhere, so replaying it would fail identically forever, while
 * every other cause here — offline, a step this app cannot draw, a handler that threw — says nothing
 * about whether the stored copy is still right. The keychain item is not the Vault, and forgetting one
 * does not touch the other.
 */
function failGrant(
  cause: AuthFailureCause,
  grant: GrantInProgress,
  source: SignInSource,
  before: readonly Command[] = []
): TransitionResult {
  const commands: Command[] = [...before];
  let reported = cause;
  if (grant.kind === 'exchangingIdToken') {
    commands.push({ do: 'cognitoSignOut' });
  }
  /**
   * The stored copy is stale, and this is the only place that can say so.
   *
   * The Authorization Server refuses both the same way — it cannot know where the password came from —
   * and the source does not survive onto the state the screen reads, so the cause has to carry the
   * distinction out of here. Without it the form told someone who touched a sensor that the email and
   * password they never entered do not match.
   *
   * Still narrowed to `nativeSignIn` even though the source alone would identify a replay. The source
   * now outlives the step, so dropping the check would extend this to a Challenge or a Token Exchange
   * failing with `invalidCredentials` — a widening that is arguably right and is not what folding the
   * old `fromStoredCredentials` flag into the source was for. Left as it was, deliberately.
   */
  if (grant.kind === 'nativeSignIn' && source === 'biometric' && cause.kind === 'invalidCredentials') {
    commands.push({ do: 'forgetCredentials' });
    reported = { kind: 'storedCredentialsRejected' };
  }
  if (cause.kind === 'stateMismatch') {
    commands.push({ do: 'reportSecurityEvent', cause });
  }
  // `noSession` rather than a reason that sounds graver: no session was created, and nothing here
  // was destroyed. `signedOut` defaults `vaultCleared` to false, which is the fact the sign-in
  // screen reads to keep offering an unlock for the Durable Root this device still holds.
  return [signedOut('noSession', reported), commands];
}

/**
 * A password sign-in, from a keyboard or from the keychain.
 *
 * The convergence the replay flow is built around: below this point one Grant and one Command, so
 * nothing in the Challenge, Token Exchange or failure paths is written twice. `fromStoredCredentials`
 * is set only for a replay, and read only by {@link failGrant}.
 */
function startNativeSignIn(email: string, password: string, source: 'password' | 'biometric'): TransitionResult {
  return [
    { tag: 'authorizing', grant: { kind: 'nativeSignIn', email }, source },
    [{ do: 'srpSignIn', email, password }],
  ];
}

function startAuthorization(provider: Provider): TransitionResult {
  return [
    // The provider *is* the source, so a federated leg cannot be labelled as anything else.
    { tag: 'authorizing', grant: { kind: 'preparingAuthorization', provider }, source: provider },
    [{ do: 'prepareAuthorizationRequest', provider }],
  ];
}

function startRestore(stale: ResourceTokens | null, trigger: RefreshTrigger): TransitionResult {
  return [{ tag: 'refreshing', stale, trigger, attempt: 0 }, [{ do: 'sessionRestore', trigger }]];
}

function exchangeIdToken(idToken: string, source: SignInSource): TransitionResult {
  return [
    { tag: 'authorizing', grant: { kind: 'exchangingIdToken', idToken }, source },
    [{ do: 'exchangeIdToken', idToken }],
  ];
}

function ignore(state: AuthState, event: AuthEvent): TransitionResult {
  return [state, [{ do: 'reportIllegal', state: describeState(state), event: event.type }]];
}

/** A stable, low-cardinality label for telemetry. Never includes token values or emails. */
export function describeState(state: AuthState): string {
  switch (state.tag) {
    case 'restoringSession':
      return `restoringSession/${state.phase.kind}`;
    case 'unauthenticated':
      return `unauthenticated/${state.flow.kind}`;
    case 'authorizing':
      return `authorizing/${state.grant.kind}`;
    case 'authorized':
      return `authorized/${state.task.kind}`;
    case 'refreshing':
      return `refreshing/${state.trigger}`;
    case 'restoreDeferred':
      return `restoreDeferred/${state.trigger}`;
  }
}
